// /catalog/types — lista de favorite_types del partner activo.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdminApiError,
  deleteFavoriteType,
  listFavoriteTypes,
  patchFavoriteType,
  type FavoriteType,
} from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { CatalogReadOnlyBanner, useCatalogContext } from '@/components/CatalogShell';
import ConfirmDialog from '@/components/ConfirmDialog';
import { readFormSchema } from '@/components/FormSchemaEditor';
import { checkboxCls } from '@/lib/form-styles';
import { canWrite as canWriteSection } from '@/lib/access';

export default function CatalogTypes(): JSX.Element {
  const { partnerId } = useCatalogContext();
  const session = useSession();
  const canWrite = canWriteSection(session, 'catalog');
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<FavoriteType | null>(null);

  const q = useQuery({
    queryKey: ['admin-favorite-types', partnerId],
    queryFn: () => listFavoriteTypes(partnerId),
    enabled: !!partnerId,
  });

  const archive = useMutation({
    mutationFn: (typeId: string) => deleteFavoriteType(typeId),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ['admin-favorite-types', partnerId] });
    },
  });

  const restore = useMutation({
    mutationFn: (typeId: string) => patchFavoriteType(typeId, { status: 'active' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-favorite-types', partnerId] }),
  });

  const types = q.data ?? [];
  const filtered = showArchived ? types : types.filter((t) => t.status === 'active');

  return (
    <div className="space-y-4">
      <CatalogReadOnlyBanner canWrite={canWrite} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="text-sm flex items-center gap-2">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className={checkboxCls()}
          />
          Mostrar archivados
        </label>
        <Link
          to="/catalog/types/new"
          className={`min-h-[2.75rem] inline-flex items-center rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 ${
            canWrite ? '' : 'pointer-events-none opacity-50'
          }`}
          aria-disabled={!canWrite}
        >
          Nuevo tipo
        </Link>
      </div>

      {q.isLoading ? <p className="text-sm text-ink-muted">Cargando…</p> : null}
      {q.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          {q.error instanceof AdminApiError ? q.error.message : 'No pudimos cargar los tipos.'}
        </div>
      ) : null}

      {q.data ? (
        <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border overflow-hidden">
          {filtered.length === 0 ? (
            <p className="p-5 text-sm text-ink-muted">
              {showArchived ? 'Sin tipos.' : 'Sin tipos activos. Crea uno para empezar.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-ink-muted border-b border-surface-border dark:border-surface-dark-border">
                    <th className="text-left px-5 py-2">Code</th>
                    <th className="text-left px-5 py-2">Nombre</th>
                    <th className="text-left px-5 py-2">Campos</th>
                    <th className="text-left px-5 py-2">Status</th>
                    <th className="text-right px-5 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => {
                    const schema = readFormSchema(t.details);
                    return (
                      <tr
                        key={t.favorite_type_id}
                        className="border-b border-surface-border dark:border-surface-dark-border last:border-b-0"
                      >
                        <td className="px-5 py-3 font-mono text-xs">{t.code}</td>
                        <td className="px-5 py-3">
                          <Link
                            to={`/catalog/types/${encodeURIComponent(t.favorite_type_id)}`}
                            className="font-medium hover:underline"
                          >
                            {t.name}
                          </Link>
                          {t.description ? (
                            <p className="text-xs text-ink-muted">{t.description}</p>
                          ) : null}
                        </td>
                        <td className="px-5 py-3 text-xs text-ink-muted">
                          {schema.fields.length === 0
                            ? <span className="opacity-50">—</span>
                            : `${schema.fields.length} campo${schema.fields.length === 1 ? '' : 's'}`}
                        </td>
                        <td className="px-5 py-3">
                          <span
                            className={`text-xs rounded-full px-2 py-0.5 font-medium ${
                              t.status === 'active'
                                ? 'bg-status-success/20 text-status-success'
                                : 'bg-ink-muted/20 text-ink-muted'
                            }`}
                          >
                            {t.status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right space-x-3">
                          {t.status === 'archived' ? (
                            <button
                              type="button"
                              onClick={() => restore.mutate(t.favorite_type_id)}
                              disabled={!canWrite || restore.isPending}
                              className="text-sm text-brand hover:underline disabled:opacity-50"
                            >
                              Restaurar
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(t)}
                              disabled={!canWrite}
                              className="text-sm text-status-error hover:underline disabled:opacity-50"
                            >
                              Archivar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Archivar tipo"
        description={
          confirmDelete ? (
            <p>
              Se va a archivar <strong>{confirmDelete.name}</strong> ({confirmDelete.code}). Los items
              y suscriptores existentes no se borran. Podes restaurar mas tarde con
              el toggle de archivados.
            </p>
          ) : null
        }
        confirmLabel="Archivar"
        danger
        loading={archive.isPending}
        onConfirm={() => confirmDelete && archive.mutate(confirmDelete.favorite_type_id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
